import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@fsd/ui';
import { useNavigate, useSearch } from '@tanstack/react-router';
import { Grid, List, Search, SlidersHorizontal } from 'lucide-react';
import { useEffect } from 'react';
import { CartSheet, Footer, Header, ProductCard, SEO } from '@/components';
import { useTranslation } from '@/i18n/hooks';
import { CategoryFilter } from './categoryFilter/categoryFilter';
import { Pagination } from './pagination';
import { useCatalog } from './useCatalog';

export function Catalog() {
  const { t, locale } = useTranslation('catalog');
  const search = useSearch({ from: '/catalog' });
  const navigate = useNavigate({ from: '/catalog' });

  useEffect(() => {
    if (search.page) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [search.page]);

  const {
    selectedCategory,
    searchQuery,
    viewMode,
    sortBy,
    showFilters,
    sortedProducts,
    paginatedProducts,
    currentPage,
    totalPages,
    itemsPerPage,
    categoryTranslationKey,
    setSearchQuery,
    setViewMode,
    setSortBy,
    setShowFilters,
    handleCategoryChange,
    handlePageChange,
  } = useCatalog(locale, search.category, search.page, (options) => {
    navigate({
      search: {
        category: options.category ?? search.category ?? 'all',
        page: options.page ?? search.page ?? 1,
      },
    });
  });

  const categoryLabel = t(categoryTranslationKey);

  return (
    <div className="min-h-screen bg-white dark:bg-slate-900">
      <SEO
        title={`Catálogo de Drones - ${categoryLabel}`}
        description={`Explore nosso catálogo completo de drones profissionais. ${categoryLabel === 'Todos' ? 'Drones novos e usados' : categoryLabel} com tecnologia de ponta para agricultura de precisão.`}
        keywords={`catálogo drones, ${categoryLabel.toLowerCase()}, drones profissionais, agricultura de precisão`}
        url={`https://fronteirasuldrones.com/catalog${selectedCategory !== 'all' ? `?category=${selectedCategory}` : ''}`}
      />
      <Header />

      <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8 mt-20">
        <div className="flex gap-8">
          {/* Mobile Filter Toggle */}
          <div className="fixed right-4 bottom-4 z-40 lg:hidden">
            <button
              type="button"
              onClick={() => setShowFilters(!showFilters)}
              className="rounded-full bg-secondary p-3 text-white shadow-lg transition-all hover:scale-105 hover:shadow-xl"
            >
              <SlidersHorizontal className="h-6 w-6" />
            </button>
          </div>

          {/* Sidebar Filters */}
          <CategoryFilter
            selectedCategory={selectedCategory}
            onCategoryChange={handleCategoryChange}
            showFilters={showFilters}
            onClose={() => setShowFilters(false)}
          />

          {/* Main Content */}
          <div className="flex-1">
            {/* Search and Controls */}
            <div className="mb-8">
              <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                {/* Search */}
                <div className="relative w-full sm:max-w-md sm:flex-1">
                  <Search className="absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2 text-slate-400" />
                  <input
                    type="text"
                    placeholder={t('searchPlaceholder')}
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full rounded-lg border border-slate-300 bg-white py-2 pl-10 pr-4 text-slate-900 placeholder-slate-400 transition-colors focus:border-secondary focus:outline-none focus:ring-2 focus:ring-secondary/20 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-100 dark:placeholder-slate-500"
                  />
                </div>

                {/* Controls */}
                <div className="flex items-center gap-3 sm:gap-4">
                  {/* Sort */}
                  <Select
                    value={sortBy}
                    onValueChange={(value) => setSortBy(value as typeof sortBy)}
                  >
                    <SelectTrigger className="w-45 rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 transition-colors focus:border-secondary focus:outline-none focus:ring-2 focus:ring-secondary/20 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-100">
                      <SelectValue placeholder={t('sortOptions.newest')} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="newest">{t('sortOptions.newest')}</SelectItem>
                      <SelectItem value="oldest">{t('sortOptions.oldest')}</SelectItem>
                      <SelectItem value="nameAsc">{t('sortOptions.nameAsc')}</SelectItem>
                      <SelectItem value="nameDesc">{t('sortOptions.nameDesc')}</SelectItem>
                    </SelectContent>
                  </Select>

                  {/* View Mode */}
                  <div className="flex rounded-lg border border-slate-300 dark:border-slate-600">
                    <button
                      type="button"
                      onClick={() => setViewMode('grid')}
                      className={`rounded-l-lg px-3 py-2 transition-colors ${
                        viewMode === 'grid'
                          ? 'bg-secondary text-white'
                          : 'bg-white text-slate-600 hover:bg-slate-50 dark:bg-slate-800 dark:text-slate-400 dark:hover:bg-slate-700'
                      }`}
                      aria-label="Grid view"
                    >
                      <Grid className="h-4 w-4" />
                    </button>
                    <button
                      type="button"
                      onClick={() => setViewMode('list')}
                      className={`rounded-r-lg border-l border-slate-300 px-3 py-2 transition-colors dark:border-slate-600 ${
                        viewMode === 'list'
                          ? 'bg-secondary text-white'
                          : 'bg-white text-slate-600 hover:bg-slate-50 dark:bg-slate-800 dark:text-slate-400 dark:hover:bg-slate-700'
                      }`}
                      aria-label="List view"
                    >
                      <List className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              </div>

              {/* Results Count */}
              <div className="mt-4 text-sm text-slate-600 dark:text-slate-400">
                {t('results.showing')}{' '}
                {sortedProducts.length > 0
                  ? `${(currentPage - 1) * itemsPerPage + 1}-${Math.min(currentPage * itemsPerPage, sortedProducts.length)}`
                  : '0'}{' '}
                {t('results.of')} {sortedProducts.length} {t('results.results')}
              </div>
            </div>

            {/* Products Grid/List */}
            {sortedProducts.length === 0 ? (
              <div className="py-12 text-center">
                <div className="mx-auto mb-4 flex h-20 w-20 items-center justify-center rounded-full bg-slate-100 dark:bg-slate-800">
                  <Search className="h-10 w-10 text-slate-400" />
                </div>
                <h3 className="mb-2 text-lg font-semibold text-slate-900 dark:text-slate-100">
                  {t('noProducts')}
                </h3>
                <p className="text-slate-600 dark:text-slate-400">
                  Tente ajustar seus filtros ou busca
                </p>
              </div>
            ) : (
              <>
                <div
                  className={`grid ${
                    viewMode === 'grid'
                      ? 'grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3'
                      : 'grid-cols-1 gap-4'
                  }`}
                >
                  {paginatedProducts.map((drone) => (
                    <ProductCard
                      key={`${drone.type}-${drone.id}`}
                      drone={drone}
                      locale={locale}
                      isUsed={drone.type === 'used'}
                      productType={drone.type}
                    />
                  ))}
                </div>

                {/* Pagination */}
                <Pagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  onPageChange={handlePageChange}
                />
              </>
            )}
          </div>
        </div>
      </div>

      <Footer />
      <CartSheet />
    </div>
  );
}
